import {
    type GroundValue,
    makeRootPath,
    type Dirt,
    type Handler,
    type IncomingSlot,
    type Namespace,
} from "@glide/computation-model-types";
import { areSetsOverlapping } from "@glide/support";
import { defined } from "@glideapps/ts-necessities";
import { setUnionInto } from "collection-utils";
import type { SubscriptionSources } from "./internal-types";

export interface SubscriptionHandler extends Handler {
    readonly needsRedraw: boolean;
}

class SubscriptionHandlerImpl implements SubscriptionHandler {
    private readonly slots: IncomingSlot[];
    public readonly isDirty = false;
    public needsRedraw = false;
    // `true` means all rows, and it implies `needsRedraw`. We might want to
    // use this at some point to only redraw the affected rows.
    private rowsNeedRedraw: Set<string> | true = new Set();

    public readonly symbolicRepresentation: string = "subscription";

    constructor(sources: SubscriptionSources, private readonly onChange: () => void) {
        this.slots = [
            ...Array.from(sources.globalKeys).map(([k, c]) => ({
                sourcePath: makeRootPath(k),
                process: (d: Dirt) => {
                    if (this.needsRedraw && this.rowsNeedRedraw === true) return false;
                    if (c !== true && d.columns !== true) {
                        if (!areSetsOverlapping(c, d.columns)) return false;
                    }
                    this.needsRedraw = true;
                    this.rowsNeedRedraw = true;
                    onChange();
                    return true;
                },
            })),
            ...Array.from(sources.columnsInRows ?? []).map(([k, m]) => {
                let allColumns: Set<string> | undefined;
                return {
                    sourcePath: makeRootPath(k),
                    process: (d: Dirt) => {
                        if (this.rowsNeedRedraw === true) return false;
                        let rowColumns: Set<string>;
                        let rowIDs: Set<string> | true;
                        if (d.rowIDs !== true) {
                            rowColumns = new Set();
                            rowIDs = new Set();
                            for (const rowID of d.rowIDs) {
                                if (m.has(rowID)) {
                                    rowIDs.add(rowID);
                                    setUnionInto(rowColumns, defined(m.get(rowID)));
                                }
                            }
                            if (rowIDs.size === 0) return false;
                        } else {
                            if (allColumns === undefined) {
                                allColumns = new Set();
                                for (const columns of m.values()) {
                                    setUnionInto(allColumns, columns);
                                }
                            }
                            rowColumns = allColumns;
                            rowIDs = true;
                        }
                        if (d.columns === true || areSetsOverlapping(d.columns, rowColumns)) {
                            this.needsRedraw = true;
                            if (rowIDs === true) {
                                this.rowsNeedRedraw = true;
                            } else {
                                setUnionInto(this.rowsNeedRedraw, rowIDs);
                            }
                            onChange();
                            return true;
                        }
                        return false;
                    },
                };
            }),
            ...Array.from(sources.columnsInAllDirectRows ?? []).map(([k, cs]) => ({
                sourcePath: makeRootPath(k),
                process: (d: Dirt) => {
                    if (this.rowsNeedRedraw === true) return false;
                    if (d.columns === true || areSetsOverlapping(d.columns, cs)) {
                        if (d.rowIDs !== true) {
                            if (d.rowIDs.size === 0) return false;
                            setUnionInto(this.rowsNeedRedraw, d.rowIDs);
                        } else {
                            this.rowsNeedRedraw = true;
                        }
                        this.needsRedraw = true;
                        onChange();
                        return true;
                    }
                    return false;
                },
            })),
            ...Array.from(sources.columnsInAllIndirectRows ?? []).map(([k, cs]) => ({
                sourcePath: makeRootPath(k),
                process: (d: Dirt) => {
                    if (this.rowsNeedRedraw === true) return false;
                    if (d.columns === true || areSetsOverlapping(d.columns, cs)) {
                        // For indirect rows, we always escalate to full redraw,
                        // but only if there are actually rows to process
                        if (d.rowIDs !== true && d.rowIDs.size === 0) return false;
                        this.rowsNeedRedraw = true;
                        this.needsRedraw = true;
                        onChange();
                        return true;
                    }
                    return false;
                },
            })),
        ];
    }

    public dependenciesAreDirty(): void {
        // We don't set ourselves dirty here.  Instead, this will trigger a
        // rehydration, which in turn will run ##recomputeSubscriptionHandlers
        // which will recompute our dependencies, which will then potentially
        // push dirt, and that will make us set ourselved dirty.
        this.onChange();
    }

    public getSlots(): readonly IncomingSlot[] {
        return this.slots;
    }

    public recompute(_ns: Namespace): GroundValue {
        return undefined;
    }

    public setDirty(): void {
        return;
    }
}

export function makeSubscriptionHandler(
    sources: SubscriptionSources,
    onChange: () => void
): SubscriptionHandler | undefined {
    if (
        sources.globalKeys.size === 0 &&
        sources.columnsInRows.size === 0 &&
        sources.columnsInAllDirectRows.size === 0 &&
        sources.columnsInAllIndirectRows.size === 0
    )
        return undefined;

    return new SubscriptionHandlerImpl(sources, onChange);
}
